/**
 * Pure, client-side password-policy checks for the native-auth reset screen.
 *
 * UX guidance only: Microsoft Entra remains the authority and may still reject a
 * password that passes these (global banned list, recently-used, fuzzy matches).
 * See the disclaimer shown under the checklist.
 */

export type PasswordRuleId = 'length' | 'complexity' | 'notCommon';

export interface PasswordRuleResult {
  id: PasswordRuleId;
  satisfied: boolean;
}

const MIN_LENGTH = 8;
const MAX_LENGTH = 256;

/** Email words shorter than this are ignored (noise like a 2-letter TLD). */
const MIN_TOKEN_LENGTH = 3;

/**
 * Generic domain/provider fragments dropped from the email-token check so they
 * don't cause false positives (e.g. "welcome" containing "com").
 */
const EMAIL_STOPWORDS = new Set([
  'com',
  'net',
  'org',
  'www',
  'mail',
  'gmail',
  'outlook',
  'hotmail',
  'yahoo',
]);

/** Org-name variants to forbid (normalized, so any case/accent is covered). */
const BANNED_WORDS = ['celeste'];

/** Lowercase + strip diacritics, so "Céleste" === "celeste". */
const normalize = (value: string): string =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Identifying words from an email (local part + domain), kept if significant. */
const emailTokens = (email: string): string[] =>
  normalize(email)
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= MIN_TOKEN_LENGTH && !EMAIL_STOPWORDS.has(token));

/** How many of the four character categories the password uses. */
const categoryCount = (password: string): number => {
  // Strip diacritics first so accented letters count as letters (é -> e), not as
  // symbols — and avoid the Unicode-property regex, which needs an ES6+ target.
  const deaccented = password.normalize('NFD').replace(/[̀-ͯ]/g, '');
  let count = 0;
  if (/[A-Z]/.test(deaccented)) count += 1; // uppercase
  if (/[a-z]/.test(deaccented)) count += 1; // lowercase
  if (/[0-9]/.test(deaccented)) count += 1; // digit
  if (/[^A-Za-z0-9]/.test(deaccented)) count += 1; // symbol / space
  return count;
};

/**
 * "Not common/easy": the password must not contain the org name (any
 * case/accent) nor a significant part of the user's email. Surfaced to the user
 * as a single generic rule — the specific terms are never shown.
 */
const isNotCommon = (password: string, email: string): boolean => {
  if (password.length === 0) {
    return false;
  }
  const normalized = normalize(password);
  const forbidden = [...BANNED_WORDS, ...emailTokens(email)];

  return !forbidden.some((word) => normalized.includes(word));
};

/** Evaluate every displayed rule against the current input. */
export const evaluatePassword = (password: string, email: string): PasswordRuleResult[] => [
  {id: 'length', satisfied: password.length >= MIN_LENGTH && password.length <= MAX_LENGTH},
  {id: 'complexity', satisfied: categoryCount(password) >= 3},
  {id: 'notCommon', satisfied: isNotCommon(password, email)},
];

/** True when every client-checkable rule passes (gates the submit button). */
export const isPasswordValid = (password: string, email: string): boolean =>
  evaluatePassword(password, email).every((rule) => rule.satisfied);

/** Length of a generated password — comfortably above the 8-char minimum. */
const GENERATED_LENGTH = 16;

// Character pools for generation. Ambiguous glyphs (0/O, 1/l/I) are excluded for
// legibility; symbols are a safe subset of the Entra-allowed set (no quote,
// backslash or space, which complicate copy/paste).
const GEN_UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const GEN_LOWER = 'abcdefghijkmnpqrstuvwxyz';
const GEN_DIGITS = '23456789';
const GEN_SYMBOLS = '@#$%&*-_!+=?';

/** Uniform random integer in [0, max) from the crypto RNG. */
const randomInt = (max: number): number => {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);

  return buffer[0] % max;
};

const pick = (pool: string): string => pool.charAt(randomInt(pool.length));

/** Fisher–Yates shuffle (in place), so the guaranteed chars aren't positional. */
const shuffle = (chars: string[]): string[] => {
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }

  return chars;
};

/**
 * Generate a strong password that satisfies every displayed rule (and avoids
 * the org name / email tokens). Seeds one char of each category, fills the rest
 * randomly, shuffles, and re-rolls on the vanishingly rare chance a rule fails.
 */
export const generatePassword = (email: string): string => {
  const all = GEN_UPPER + GEN_LOWER + GEN_DIGITS + GEN_SYMBOLS;

  for (let attempt = 0; attempt < 20; attempt += 1) {
    const chars = [pick(GEN_UPPER), pick(GEN_LOWER), pick(GEN_DIGITS), pick(GEN_SYMBOLS)];
    while (chars.length < GENERATED_LENGTH) {
      chars.push(pick(all));
    }

    const candidate = shuffle(chars).join('');
    if (isPasswordValid(candidate, email)) {
      return candidate;
    }
  }

  // Deterministic fallback (essentially never reached).
  return `${pick(GEN_UPPER)}${pick(GEN_LOWER)}${pick(GEN_DIGITS)}${pick(GEN_SYMBOLS)}${randomInt(
    1e9
  )}`;
};
